"use client"
import type { ComponentProps, SyntheticEvent } from "react"
import { useMemo, useState } from "react"
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  Field,
  FieldLabel,
  useComboboxAnchor,
} from "../ui"
import FieldDescriptionOrError from "./FieldDescriptionOrError"

type Props = {
  options: string[]
  onChange: (values: string[]) => void
  value: string[]
  allowAdd?: boolean
  label?: string
  placeholder?: string
  className?: string
  isError?: boolean
  error?: string | null
  helperText?: string
  empty?: string
  chipsProps?: Omit<ComponentProps<typeof ComboboxChips>, "key">
}

export default function AutocompleteChips({
  options,
  onChange,
  value,
  allowAdd = true,
  label,
  placeholder,
  className,
  isError,
  error,
  helperText,
  empty = "No items available.",
  chipsProps,
}: Props) {
  const anchor = useComboboxAnchor()
  const [input, setInput] = useState("")

  const items = useMemo(
    () => [...options, ...value.filter((v) => !options.includes(v))].toSorted(),
    [options, value],
  )

  const createOption = useMemo(() => {
    if (!allowAdd || !input.trim()) {
      return null
    }
    const exists = items.some(
      (o) => o.toLowerCase() === input.trim().toLowerCase(),
    )

    return exists ? null : input.trim()
  }, [allowAdd, input, items])

  const handleNew = (e: SyntheticEvent) => {
    e.preventDefault()
    onChange([...value, input.trim()])
    setInput("")
  }

  return (
    <Field className={className}>
      {!!label && <FieldLabel>{label}</FieldLabel>}
      <Combobox
        multiple
        autoHighlight
        items={items}
        inputValue={input}
        onInputValueChange={setInput}
        value={value}
        onValueChange={(values) => {
          setInput("")
          onChange(values)
        }}
      >
        <ComboboxChips ref={anchor} {...chipsProps}>
          <ComboboxValue>
            {(values: string[]) => (
              <>
                {values.map((value) => (
                  <ComboboxChip key={value}>{value}</ComboboxChip>
                ))}
                <ComboboxChipsInput
                  placeholder={!value.length ? placeholder : undefined}
                  aria-invalid={isError ?? !!error}
                  onKeyDown={(e) => {
                    if (createOption && e.key === "Enter") {
                      handleNew(e)
                    }
                  }}
                />
              </>
            )}
          </ComboboxValue>
        </ComboboxChips>
        <ComboboxContent anchor={anchor}>
          <ComboboxEmpty>{!createOption && empty}</ComboboxEmpty>
          <ComboboxList>
            {(option: string) => (
              <ComboboxItem
                key={option}
                value={option}
                className="cursor-pointer"
              >
                {option}
              </ComboboxItem>
            )}
          </ComboboxList>
          {createOption && (
            <div className="border-t border-border p-1">
              <button
                type="button"
                className="flex w-full cursor-pointer items-center rounded-md py-1 pr-8 pl-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                onPointerDown={handleNew}
              >
                {createOption}
              </button>
            </div>
          )}
        </ComboboxContent>
      </Combobox>
      <FieldDescriptionOrError description={helperText} error={error} />
    </Field>
  )
}
